import { Component, HostBinding, Input } from '@angular/core';
import { AIChatbotMessage } from '../../models/ai-chatbot-message';
import { AIChatbotService } from '../../services/ai-chatbot/ai-chatbot.service';
import { AiChatbotTemplateTunerGeneration } from '../../models/ai-chatbot-presentation-data';
import { AIChatbotFeedbackLoggingService } from '../../services/ai-chatbot-feedback-logging/ai-chatbot-feedback-logging.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';

@Component({
  selector: 'ai-chatbot-message',
  templateUrl: './ai-chatbot-message.component.html',
  styleUrls: ['./ai-chatbot-message.component.scss'],
  standalone: false,
})
export class AiChatbotMessageComponent {
  @Input({ required: true }) message!: AIChatbotMessage;

  @HostBinding('class.ai-chatbot-message-container-user')
  get isUserMessage(): boolean {
    return this.message?.role === 'user';
  }

  constructor(
    public aiChatbotService: AIChatbotService,
    private feedbackLoggingService: AIChatbotFeedbackLoggingService,
    private trackerService: TrackerService,
  ) {}

  getTemplateTunerVariations(): AiChatbotTemplateTunerGeneration[] {
    if (
      this.message.content?.type === 'template_tuner_create' ||
      this.message.content?.type === 'template_tuner_retrieve'
    ) {
      return this.message.content.data[0];
    }

    return [];
  }

  openTemplateTunerPreview(
    presentation: AiChatbotTemplateTunerGeneration,
    index: number,
  ): void {
    this.aiChatbotService.setSelectedTemplateTunerPreview(
      this.message.id,
      index,
      presentation,
    );
  }

  isTemplateTunerOptionSelected(optionIndex: number): boolean {
    const selected = this.aiChatbotService.selectedTemplateTunerPreview();
    return (
      selected !== null &&
      selected.messageId === this.message.id &&
      selected.optionIndex === optionIndex
    );
  }

  onThumbsUpClick(isActivated: boolean): void {
    // Only log when thumbs up is activated (not when deactivated)
    if (isActivated) {
      this.logFeedback('good');
    }
  }

  onFeedbackSent(feedbackText: string): void {
    // Log negative feedback with message to BigQuery
    this.logFeedback('bad', feedbackText);

    this.trackerService.presentationEvent(
      'AI Chatbot Negative Feedback',
      this.aiChatbotService.currentSession()?.id,
    );
  }

  private logFeedback(rating: 'good' | 'bad', message?: string): void {
    const session = this.aiChatbotService.currentSession();
    if (!session) {
      console.warn('No active session for feedback logging');
      return;
    }

    const messages = session.messages();
    const messageIdx = messages.findIndex((msg) => msg.id === this.message.id);

    if (messageIdx === -1) {
      console.warn('Message not found in session for feedback logging');
      return;
    }

    this.feedbackLoggingService
      .logFeedback(session.id, messageIdx, this.message, rating, message)
      .subscribe({
        next: () => {
          // Success - feedback logged
        },
        error: (error) => {
          console.error('Error logging feedback to BigQuery:', error);
        },
      });
  }
}
